<template>
  <el-dialog
    :visible="visible"
    @update:visible="handleVisibleChange"
    :before-close="handleClose"
    width="1200px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    custom-class="upload-courseware-dialog"
    class="upload-courseware-dialog_wrap"
    :appendToBody="appendToBody"
  >
    <!-- 弹窗头部 -->
    <div class="dialog-header">
      <h3 class="dialog-title">添加课件</h3>
      <div class="close-btn" @click="handleClose">
        <img
          src="@saas/components/assets/images/courseware/close.png"
          alt="关闭"
        />
      </div>
    </div>

    <!-- 弹窗内容 -->
    <div class="dialog-content">
      <!-- 左侧区域 -->
      <div class="left-panel">
        <!-- 搜索框 -->
        <div class="search-container">
          <div class="search-input-wrapper">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索课件标题"
              maxlength="50"
              class="search-input"
              @input="handleSearch"
            />
            <img
              src="@saas/components/assets/images/courseware/search.png"
              alt="搜索"
              class="search-icon"
              @click="handleSearch"
            />
          </div>
        </div>

        <!-- Tab切换 -->
        <!-- <div class="tab-container">
          <div
            class="tab-item"
            :class="{ active: activeTab === 'my' }"
            @click="handleTabChange('my')"
          >
            我的课件
          </div>
          <div
            class="tab-item"
            :class="{ active: activeTab === 'org' }"
            @click="handleTabChange('org')"
          >
            机构课件
          </div>
        </div> -->

        <!-- 文件列表 -->
        <div
          class="file-list-container"
          v-infinite-scroll="loadMore"
          :infinite-scroll-disabled="loading || !hasMore"
          :infinite-scroll-distance="100"
          :infinite-scroll-immediate="false"
        >
          <div v-if="fileList.length === 0" class="empty-state">
            <img
              src="@saas/components/assets/images/courseware/no-data.png"
              alt="暂无数据"
            />
            <p>暂无课件</p>
          </div>
          <div v-else class="file-list">
            <div
              v-for="file in fileList"
              :key="file.id"
              class="file-item"
              :class="{
                'file-item-hover': hoveredFile === file.id,
                'file-item-selected': selectedFile?.id === file.id,
              }"
              @mouseenter="hoveredFile = file.id"
              @mouseleave="hoveredFile = null"
            >
              <div class="info-box" @click="handleFileSelect(file)">
                <!-- 文件图标 -->
                <div class="file-icon">
                  <img :src="getFileIcon(file)" alt="文件图标" />
                </div>

                <!-- 文件信息 -->
                <div class="file-info">
                  <div
                    v-if="editingFile?.id !== file.id"
                    class="file-name"
                    :title="file.file_name"
                    @dblclick="handleEditFile(file)"
                  >
                    {{ file.file_name }}
                  </div>
                  <input
                    v-else
                    :ref="`editInput_${file.id}`"
                    v-model="editingFileName"
                    class="file-name-input"
                    @blur="handleSaveFileName"
                    @keydown.enter="handleEnterKey"
                    @keyup.esc="handleCancelEdit"
                    maxlength="50"
                  />
                </div>
                <!-- 编辑按钮 -->
                <div
                  v-show="
                    hoveredFile === file.id && editingFile?.id !== file.id
                  "
                  class="edit-btn"
                  @click.stop="handleEditFile(file)"
                >
                  <img
                    src="@saas/components/assets/images/courseware/edit.png"
                    alt="编辑"
                  />
                </div>
              </div>

              <span class="new-tag">
                <img
                  v-if="isTodayUpload(file.created_at)"
                  src="@saas/components/assets/images/courseware/new.png"
                  alt="NEW"
                />
              </span>
            </div>

            <!-- 加载状态 -->
            <div v-if="loading" class="loading-more">
              <i class="el-icon-loading"></i>
              <span>加载中...</span>
            </div>
          </div>
        </div>

        <!-- 上传区域 -->
        <div class="upload-container">
          <!-- 默认状态 -->
          <div
            v-if="uploadStatus === 'default'"
            class="upload-default"
            @click="handleUploadClick"
          >
            <div class="upload-btn">
              <img
                src="@saas/components/assets/images/courseware/upload.png"
                alt="上传"
              />
            </div>
            <div class="upload-info">
              <h4>上传课件</h4>
              <p>讲义支持 PPT、PDF、Word、图片及视频格式，大小不超过 80M</p>
            </div>
          </div>

          <!-- 上传中状态 -->
          <div
            v-else-if="uploadStatus === 'uploading'"
            class="upload-uploading"
          >
            <div class="upload-progress">
              <span>上传中</span>
              <span class="progress-text">{{ uploadProgress }}%</span>
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: uploadProgress + '%' }"
                ></div>
              </div>
            </div>
            <div class="cancel-btn" @click="handleCancelUpload">取消</div>
          </div>

          <!-- 成功状态 -->
          <div v-else-if="uploadStatus === 'success'" class="upload-success">
            <div class="success-info">
              <img
                src="@saas/components/assets/images/courseware/success.png"
                alt="成功"
              />
              <span>上传成功</span>
            </div>
            <div class="continue-btn" @click="handleContinueUpload">
              继续上传
            </div>
          </div>

          <!-- 失败状态 -->
          <div v-else-if="uploadStatus === 'failed'" class="upload-failed">
            <div class="failed-info">
              <img
                src="@saas/components/assets/images/courseware/fail.png"
                alt="失败"
              />
              <span>上传失败</span>
            </div>
            <div class="retry-btn" @click="handleRetryUpload">重新上传</div>
          </div>
        </div>
      </div>

      <!-- 分隔线 -->
      <div class="divider"></div>

      <!-- 右侧预览区域 -->
      <div class="right-panel">
        <div class="preview-container">
          <div v-if="!selectedFile" class="preview-empty">
            <p>课件预览区域</p>
          </div>
          <div
            v-else-if="isVideo(selectedFile.file_name)"
            class="preview-video"
          >
            <video
              ref="videoPlayer"
              :src="selectedFile.transcode_result"
              controls
            >
              <source :src="selectedFile.transcode_result" />
            </video>
          </div>
          <div
            v-else-if="isImage(selectedFile.file_name)"
            class="preview-image"
          >
            <img :src="selectedFile.transcode_result" alt="" />
          </div>
          <div v-else class="preview-content">
            <!-- 预览图片 -->
            <div class="preview-image">
              <img
                :src="selectedFile.thumbnail + currentPage + '.jpg'"
                alt="预览"
                class="no-select"
              />
            </div>

            <!-- 页码控制 -->
            <template v-if="selectedFile.pages > 1">
              <div
                v-show="currentPage > 1"
                class="page-btn page-btn_pre"
                @click="handlePrevPage"
              >
                <img
                  src="@saas/components/assets/images/courseware/arrow-left.png"
                  alt=""
                  class="no-select"
                />
              </div>
              <div class="page-info">
                {{ currentPage }}/{{ selectedFile.pages }}
              </div>
              <div
                class="page-btn page-btn_next"
                v-show="currentPage < selectedFile.pages"
                @click="handleNextPage"
              >
                <img
                  src="@saas/components/assets/images/courseware/arrow-right.png"
                  alt=""
                  class="no-select"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 弹窗底部 -->
    <div class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        :disabled="!(selectedFile && selectedFile.id)"
        @click="handleConfirm"
      >
        使用课件
      </el-button>
    </div>

    <!-- 文件选择器 -->
    <input
      ref="fileInput"
      type="file"
      accept=".jpg, .jpeg, .png, .gif, .bmp, .ppt,.pptx,.pdf,.doc,.docx, .mp4, .avi, .wmv, .mov, .flv, .rmvb, .3gp, .m4v, .mkv, .webm"
      style="display: none"
      @change="handleFileChange"
    />

    <!-- 确认取消上传弹窗 -->
    <mg-dialog
      :show.sync="cancelUploadDialogVisible"
      title="是否取消文件上传?"
      size="small"
      class="close-dialog"
      :append-to-body="true"
      :show-close="false"
    >
      <div class="footer-btn">
        <el-button @click="cancelUploadDialogVisible = false" size="small">
          取消
        </el-button>
        <el-button
          type="primary"
          @click="handleConfirmCancelUpload"
          size="small"
        >
          确定
        </el-button>
      </div>
    </mg-dialog>
  </el-dialog>
</template>

<script>
import {
  getMyCoursewareList,
  updateDocName,
  // getOrgCoursewareList,
} from "@saas/server-api/src/courseware/index.js";
import { fileImagebyFileName, isImage, isVideo, isDoc } from "@saas/components";
import { uploadAndTranscodeFile } from "@saas/components/src/utils/cos-upload";
import MgDialog from "@saas/components/src/components/mg-dialog/Index.vue";
import { checkPermission } from "@saas/components/src/utils/permission";
export default {
  name: "UploadCourseware",
  components: {
    MgDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // 搜索相关
      searchKeyword: "",
      searchTimer: null,

      // Tab相关
      activeTab: "my", // 'my' | 'org'

      // 文件列表相关
      fileList: [],
      pageNo: 1,
      pageSize: 20,
      hasMore: true,
      loading: false,

      // 选中文件
      selectedFile: null,
      hoveredFile: null,

      // 预览相关
      currentPage: 1,

      // 上传相关
      uploadStatus: "default", // 'default' | 'uploading' | 'success' | 'failed'
      uploadProgress: 0,
      uploadFile: null,
      uploadController: null,
      uploadCosInstance: null, // 保存上传的cos实例

      // 编辑相关
      editingFile: null,
      editingFileName: "",

      // 弹窗相关
      cancelUploadDialogVisible: false,
    };
  },
  computed: {
    materialType() {
      if (this.activeTab === "my") {
        return 6;
      } else {
        return 5;
      }
    },
    uploadPermission() {
      return checkPermission("action:material:live:private:manage");
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.init();
      }
    },
    activeTab() {
      this.resetList();
      this.loadFileList();
    },
  },
  methods: {
    // 初始化
    async init() {
      this.resetData();
      await this.loadFileList();
      if (this.fileList.length) {
        this.selectedFile = this.fileList[0];
        this.currentPage = 1;
      }
    },

    // 重置数据
    resetData() {
      this.searchKeyword = "";
      this.activeTab = "my";
      this.fileList = [];
      this.pageNo = 1;
      this.hasMore = true;
      this.selectedFile = null;
      this.hoveredFile = null;
      this.currentPage = 1;
      this.uploadStatus = "default";
      this.uploadProgress = 0;
      this.uploadCosInstance = null;
    },

    // 重置列表
    resetList() {
      this.fileList = [];
      this.pageNo = 1;
      this.hasMore = true;
      this.selectedFile = null;
    },

    // 加载文件列表
    async loadFileList() {
      if (this.loading) return;

      this.loading = true;
      try {
        const params = {
          page_no: this.pageNo,
          page_size: this.pageSize,
          doc_name: this.searchKeyword,
        };

        let response;
        if (this.activeTab === "my") {
          response = await getMyCoursewareList(params);
        } else {
          // response = await getOrgCoursewareList(params);
        }

        const { data, code, message } = response;
        if (code === 1) {
          const filteredData = this.filterData(data.data || data.list);

          if (this.pageNo === 1) {
            this.fileList = filteredData;
          } else {
            this.fileList.push(...filteredData);
          }

          this.hasMore = this.fileList.length < data.total;
        } else {
          this.$darkMessage.error(message);
        }
      } catch (error) {
        console.error("加载文件列表失败:", error);
      } finally {
        this.loading = false;
      }
    },

    // 过滤数据（只显示转码成功的数据）
    filterData(data) {
      if (this.activeTab === "my") {
        return data.map((item) => {
          return {
            file_name: item.doc_name,
            created_at:
              item.created_at || item.created_time || item.upload_time,
            ...item,
          };
        });
      } else {
        return data.map((item) => {
          return {
            file_name: item.material_name + item.file_ext,
            created_at:
              item.created_at || item.created_time || item.upload_time,
            ...item,
          };
        });
      }
    },

    // 搜索处理
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      this.searchTimer = setTimeout(() => {
        this.resetList();
        this.loadFileList();
      }, 300);
    },

    // Tab切换
    handleTabChange(tab) {
      this.activeTab = tab;
    },

    // 文件选择
    handleFileSelect(file) {
      this.selectedFile = file;
      this.currentPage = 1;
    },

    // 文件编辑
    handleEditFile(file) {
      this.editingFile = file;
      // 提取文件名（不含后缀）
      const fileNameWithoutExt = this.getFileNameWithoutExtension(
        file.file_name
      );
      this.editingFileName = fileNameWithoutExt;
      this.$nextTick(() => {
        // 使用setTimeout确保DOM更新完成后再聚焦
        setTimeout(() => {
          const input = this.$refs[`editInput_${file.id}`];
          if (input && input[0]) {
            input[0].focus();
            // 移除自动全选功能
            // input[0].select();
          }
        }, 0);
      });
    },

    // 保存文件名
    async handleSaveFileName() {
      const editingFile = this.editingFile;
      const editingFileName = this.editingFileName;
      // 防止重复执行
      if (!editingFile) {
        return;
      }
      if (!editingFileName.trim()) {
        // this.$darkMessage.warning("文件名不能为空");
        this.editingFileName = this.getFileNameWithoutExtension(
          editingFile.file_name
        );
        this.editingFile = null;
        return;
      }

      // 如果文件名没有变化，直接退出编辑状态
      const originalFileNameWithoutExt = this.getFileNameWithoutExtension(
        editingFile.file_name
      );
      if (editingFileName === originalFileNameWithoutExt) {
        this.editingFile = null;
        return;
      }

      try {
        // 调用编辑文件名接口，传给后端不含后缀的文件名
        const res = await updateDocName({
          id: editingFile.id,
          doc_name: editingFileName,
        });
        if (res.code === 1) {
          // 更新本地数据，显示时包含后缀
          const file = this.fileList.find((item) => item.id === editingFile.id);
          if (file) {
            const fileExtension = this.getFileExtension(editingFile.file_name);
            file.file_name = editingFileName + fileExtension;
          }
          this.$darkMessage.success("文件名修改成功");
          this.editingFile = null;
        } else {
          this.$darkMessage.error(res.message);
          this.editingFileName = this.getFileNameWithoutExtension(
            this.editingFile.file_name
          );
          this.editingFile = null;
        }
      } catch (error) {
        console.error("修改文件名失败:", error);
        this.$darkMessage.error("修改文件名失败");
        this.editingFileName = this.getFileNameWithoutExtension(
          this.editingFile.file_name
        );
        this.editingFile = null;
      }
    },

    // 处理回车键
    handleEnterKey(event) {
      // 阻止默认行为
      event.preventDefault();
      // 触发失焦事件
      event.target.blur();
    },

    // 取消编辑
    handleCancelEdit() {
      this.editingFileName = this.getFileNameWithoutExtension(
        this.editingFile.file_name
      );
      this.editingFile = null;
    },

    // 加载更多
    loadMore() {
      if (this.loading || !this.hasMore) return;

      this.pageNo++;
      this.loadFileList();
    },

    // 上传点击
    handleUploadClick() {
      if (this.uploadPermission) {
        this.$refs.fileInput.click();
      } else {
        this.$darkMessage.error("您没有权限，请联系你们的管理员。");
      }
    },

    // 文件选择
    handleFileChange(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file) return;

      // 验证文件格式
      const allowedTypes = [
        "application/pdf",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (
        !allowedTypes.includes(file.type) &&
        !this.isImage(file.name) &&
        !this.isVideo(file.name)
      ) {
        this.$darkMessage.error("只支持PPT、PDF、Word、图片和视频格式文件");
        return;
      }
      // 验证文件大小（80MB = 80 * 1024 * 1024 bytes）
      const maxSize = 80 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$darkMessage.error("文件大小不能超过80MB");
        return;
      }
      this.uploadFile = file;
      this.startUpload();
    },

    // 开始上传
    async startUpload() {
      this.uploadStatus = "uploading";
      this.uploadProgress = 0;

      try {
        this.uploadCosInstance = await uploadAndTranscodeFile(
          this.uploadFile,
          {
            type: this.materialType,
            isNeedTranscode: !this.isImage(this.uploadFile.name),
          },
          {
            onProgress: (progress) => {
              this.uploadProgress = (progress * 100).toFixed(0);
            },
            onTranscodeComplete: (data) => {
              console.log("onTranscodeComplete", data);
              this.uploadProgress = 100;
              this.uploadStatus = "success";
              this.refreshFileList(data.materialId);
            },
            onError: () => {
              this.uploadStatus = "failed";
            },
          }
        );
      } catch (error) {
        console.error("上传失败:", error);
        this.uploadStatus = "failed";
        this.$darkMessage.error("上传失败");
      }
    },

    // 取消上传
    handleCancelUpload() {
      // 取消上传任务
      if (this.uploadCosInstance) {
        this.uploadCosInstance.executeCancelTask();
        this.uploadCosInstance = null;
      }

      this.uploadStatus = "default";
      this.uploadProgress = 0;
      this.uploadFile = null;
    },

    // 继续上传
    handleContinueUpload() {
      this.uploadStatus = "default";
      this.handleUploadClick();
    },

    // 重新上传
    handleRetryUpload() {
      this.uploadStatus = "default";
      this.handleUploadClick();
    },

    // 刷新文件列表
    async refreshFileList(id) {
      this.resetList();
      await this.loadFileList();
      const selectedFile =
        this.fileList.find((item) => item.material_id === id) || null;
      if (selectedFile) {
        this.selectedFile = selectedFile;
        this.currentPage = 1;
      }
    },

    // 上一页
    handlePrevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    // 下一页
    handleNextPage() {
      if (this.currentPage < this.selectedFile.pages) {
        this.currentPage++;
      }
    },
    // 判断是否为今天上传
    isTodayUpload(createdAt) {
      if (!createdAt) return false;

      try {
        const today = new Date();
        const uploadDate = new Date(createdAt * 1000);

        // 检查日期是否有效
        if (isNaN(uploadDate.getTime())) return false;

        return today.toDateString() === uploadDate.toDateString();
      } catch (error) {
        console.warn("日期解析失败:", createdAt, error);
        return false;
      }
    },

    // 处理visible变化
    handleVisibleChange(newVisible) {
      this.$emit("update:visible", newVisible);
    },

    // 关闭弹窗
    handleClose() {
      // 如果video正在播放，需要先关闭video播放
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
      }
      if (this.uploadStatus === "uploading") {
        this.cancelUploadDialogVisible = true;
      } else {
        this.$emit("update:visible", false);
        this.$emit("close");
      }
    },

    // 确认取消上传
    handleConfirmCancelUpload() {
      // 取消上传任务
      if (this.uploadCosInstance) {
        this.uploadCosInstance.executeCancelTask();
        this.uploadCosInstance = null;
      }

      this.uploadStatus = "default";
      this.uploadProgress = 0;
      this.uploadFile = null;
      this.cancelUploadDialogVisible = false;
      this.$emit("update:visible", false);
    },

    // 确认选择
    handleConfirm() {
      if (!this.selectedFile) {
        this.$darkMessage.warning("请选择课件");
        return;
      }

      if (this.uploadStatus === "uploading") {
        this.$darkMessage.warning("请等待文件上传完成");
        return;
      }
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
      }
      this.$emit("confirm", this.selectedFile);
      // this.$emit("update:visible", false);
    },
    // 获取文件名（不含后缀）
    getFileNameWithoutExtension(fileName) {
      const lastDotIndex = fileName.lastIndexOf(".");
      if (lastDotIndex === -1) {
        return fileName;
      }
      return fileName.substring(0, lastDotIndex);
    },

    // 获取文件后缀
    getFileExtension(fileName) {
      const lastDotIndex = fileName.lastIndexOf(".");
      if (lastDotIndex === -1) {
        return "";
      }
      return fileName.substring(lastDotIndex);
    },

    getFileIcon(file) {
      return fileImagebyFileName(file.file_name);
    },
    isVideo(fileName) {
      return isVideo(fileName);
    },
    isImage(fileName) {
      return isImage(fileName);
    },
    isDoc(fileName) {
      return isDoc(fileName);
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-courseware-dialog_wrap ::v-deep {
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 0;
    background: #ffffff;
    border-radius: 24px;
  }
  .upload-courseware-dialog {
    border-radius: 24px !important;
    margin-top: 10vh !important;
    .el-dialog__body {
      padding: 0;
    }
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 34px 40px 24px 40px;
  .dialog-title {
    height: 28px;
    line-height: 28px;
    font-size: 20px;
    font-weight: 500;
    color: #0d1400;
    margin: 0;
  }

  .close-btn {
    cursor: pointer;
    position: absolute;
    right: 27px;
    top: 27px;
    img {
      width: 22px;
      height: 22px;
    }
  }
}

.dialog-content {
  display: flex;
  height: 446px;
  margin: 0 40px;
  border-radius: 12px;
  border: 1px solid #e5e6e8;
  .left-panel {
    width: 277px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;
    .search-container {
      .search-input-wrapper {
        position: relative;
        .search-input {
          width: 100%;
          height: 32px;
          padding: 0 32px 0 12px;
          border: 1px solid #f5f5f6;
          border-radius: 8px;
          font-size: 12px;
          color: #747573;
          outline-color: #00c89d;
          &:focus-visible {
            border-color: #f5f5f6;
          }
          &::placeholder {
            color: #747573;
          }
        }

        .search-icon {
          cursor: pointer;
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
        }
      }
    }

    .tab-container {
      display: flex;
      .tab-item {
        padding: 7px 12px;
        font-size: 12px;
        color: #515350;
        cursor: pointer;
        border-radius: 4px;
        margin-right: 8px;
        &.active {
          color: #00c89d;
          font-weight: 600;
        }
      }
    }

    .file-list-container {
      flex: 1;
      overflow-y: auto;
      margin-bottom: 8px;
      padding: 4px 0;

      // 自定义滚动条样式
      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: #e5e6e8;
        border-radius: 2px;

        &:hover {
          background: #c9cdd4;
        }
      }

      &::-webkit-scrollbar-corner {
        background: transparent;
      }

      // Firefox滚动条样式
      scrollbar-width: thin;
      scrollbar-color: #e5e6e8 transparent;
      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 200px;
        color: #515350;
        font-size: 12px;

        img {
          width: 100px;
          height: 100px;
          margin-bottom: 12px;
        }
      }

      .file-list {
        .file-item {
          display: flex;
          align-items: center;
          position: relative;
          margin-bottom: 2px;
          height: 40px;
          box-sizing: border-box;
          &.file-item-selected {
            .info-box {
              background-color: #d5fcf4;
            }
          }
          .info-box {
            display: flex;
            align-items: center;
            padding: 0 12px;
            border-radius: 6px;
            cursor: pointer;
            position: relative;
            margin-bottom: 2px;
            height: 40px;
            box-sizing: border-box;
            flex: 1;
            max-width: 220px;
            &:hover {
              background-color: #e5e6e8;
            }
          }
          .file-icon {
            width: 20px;
            height: 20px;
            margin-right: 12px;
            flex-shrink: 0;

            img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
          }

          .file-info {
            flex: 1;
            min-width: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            overflow: hidden; // 确保内容不会溢出容器
            cursor: pointer;
            .file-name {
              flex: 1;
              font-size: 14px;
              color: #0d1400;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              min-width: 0;
              max-width: 145px; // 设置最大宽度
              margin-right: 8px;
              word-break: break-all;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }

            .file-name-input {
              flex: 1;
              font-size: 14px;
              color: #0d1400;
              border: none;
              outline: none;
              min-width: 0;
              font-family: inherit;
              word-break: break-all;
              word-wrap: break-word;
              overflow-wrap: break-word;
              width: 140px;
              height: 26px;
              background: #f4f4f5;
              border-radius: 6px;
              padding: 0 2px;
            }
          }

          .edit-btn {
            opacity: 0;
            transition: opacity 0.2s;
            padding: 4px;
            margin-left: 8px;
            flex-shrink: 0;

            img {
              width: 16px;
              height: 16px;
            }
          }

          &:hover .edit-btn {
            opacity: 1;
          }
          .new-tag {
            flex-shrink: 0;
            width: 24px;
            margin-left: 6px;
            img {
              width: 24px;
              height: 14px;
            }
          }
        }

        .loading-more {
          text-align: center;
          padding: 12px;
          color: #909399;
          font-size: 12px;
          display: flex;
          align-items: center;
          justify-content: center;

          i {
            margin-right: 4px;
            font-size: 14px;
          }
        }
      }
    }

    .upload-container {
      height: 69px;
      border: 1px dashed #efefef;
      border-radius: 8px;
      background: linear-gradient(
        135deg,
        #fbfffb 0%,
        #f8fdff 48.98%,
        #eef5ff 100%
      );
      display: flex;
      align-items: center;
      padding: 0 12px;
      cursor: pointer;
      .upload-default {
        display: flex;
        align-items: center;
        width: 100%;

        .upload-info {
          flex: 1;

          h4 {
            font-size: 16px;
            font-weight: 600;
            color: #1d2129;
            margin: 0 0 4px 0;
          }

          p {
            font-size: 10px;
            color: #4e5969;
            margin: 0;
          }
        }

        .upload-btn {
          margin-right: 12px;
          img {
            width: 26px;
            height: 26px;
          }
        }
      }

      .upload-uploading {
        display: flex;
        align-items: center;
        width: 100%;

        .upload-progress {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
          color: #0d1400;
          .progress-text {
            width: 38px;
          }
          .progress-bar {
            width: 117px;
            height: 6px;
            background-color: #e1e1e1;
            border-radius: 3px;
            overflow: hidden;

            .progress-fill {
              height: 100%;
              background: linear-gradient(90deg, #00eb81 0%, #00c89d 100%);
              transition: width 0.3s;
            }
          }
        }

        .cancel-btn {
          color: #f03349;
          font-size: 14px;
          cursor: pointer;
          margin-left: 9px;
        }
      }

      .upload-success,
      .upload-failed {
        display: flex;
        align-items: center;
        width: 100%;

        .success-info,
        .failed-info {
          display: flex;
          align-items: center;
          flex: 1;

          img {
            width: 16px;
            height: 16px;
            margin-right: 8px;
          }

          span {
            font-size: 14px;
            color: #0d1400;
          }
        }

        .continue-btn,
        .retry-btn {
          color: #00c89d;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }
      }
    }
  }

  .divider {
    width: 1px;
    background-color: #efefef;
  }

  .right-panel {
    flex: 1;
    padding: 24px 60px;
    position: relative;
    // 自定义滚动条样式
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #e5e6e8;
      border-radius: 2px;

      &:hover {
        background: #c9cdd4;
      }
    }

    &::-webkit-scrollbar-corner {
      background: transparent;
    }

    // Firefox滚动条样式
    scrollbar-width: thin;
    scrollbar-color: #e5e6e8 transparent;

    .preview-container {
      height: 100%;
      border: 1px solid #e5e6e8;
      border-radius: 12px;
      overflow: hidden;
      background-color: #f4f4f5;
      display: flex;
      flex-direction: column;

      .preview-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #515350;
        font-size: 16px;
      }

      .preview-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        // 自定义滚动条样式
        &::-webkit-scrollbar {
          width: 4px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: #e5e6e8;
          border-radius: 2px;

          &:hover {
            background: #c9cdd4;
          }
        }

        &::-webkit-scrollbar-corner {
          background: transparent;
        }

        // Firefox滚动条样式
        scrollbar-width: thin;
        scrollbar-color: #e5e6e8 transparent;

        .preview-image {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          background: #fff9ef;
          img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }
        }

        .page-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.2);
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .arrow-left,
          .arrow-right {
            color: #ffffff;
            font-size: 20px;
            font-weight: bold;
            line-height: 1;
          }
          &.page-btn_pre {
            left: 14px;
          }
          &.page-btn_next {
            right: 14px;
          }
        }

        .page-info {
          position: absolute;
          width: 44px;
          height: 16px;
          background: #000000;
          border-radius: 8px;
          opacity: 0.5;
          bottom: 36px;
          left: 50%;
          transform: translate(-50%);
          font-weight: 500;
          font-size: 12px;
          color: #ffffff;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
      .preview-video,
      .preview-image {
        flex: 1;
        overflow: hidden;
        video {
          width: 100%;
          height: 100%;
        }
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 20px 24px;

  .el-button {
    min-width: 78px;
    height: 36px;
    border-radius: 8px;
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    &--default {
      background: #f2f3f5;
      border: none;
      color: #0d1400;
      &:hover {
        color: #0d1400;
      }
    }
    &--primary {
      background: linear-gradient(135deg, #00eb81 0%, #00c89d 100%);
      border: none;
      &:hover,
      &:focus {
        background: linear-gradient(
          135deg,
          #00eb81 0%,
          #00c89d 100%
        ) !important;
      }
      &:disabled {
        background: #f2f3f5;
        color: #c9cdd4;
      }
    }
  }
}
.close-dialog ::v-deep {
  .footer-btn {
    text-align: center;
  }
}
</style>
